import type { NodeOverlay } from '@bliks-io/ui';
import type { CapacityBinding, ProcessStepRecordingDisplay, Survey } from '@bliks-io/contracts';
import { meanSurveyMetric } from './surveyMetrics';

export const HEATMAP_VIEW_MODES = ['standard', 'rating', 'capacity', 'combined'] as const;

export type HeatmapViewMode = (typeof HEATMAP_VIEW_MODES)[number];

export const RATING_GREEN_MIN = 4.0;
export const RATING_YELLOW_MIN = 2.5;
export const CAPACITY_GREEN_MAX_SHARE = 1 / 3;
export const CAPACITY_YELLOW_MAX_SHARE = 2 / 3;
export const COMBINED_GREEN_MIN = 2 / 3;
export const COMBINED_YELLOW_MIN = 1 / 3;
const COMBINED_RATING_WEIGHT = 0.6;
const COMBINED_CAPACITY_WEIGHT = 0.4;

type RatedSurvey = { rating?: Pick<NonNullable<Survey['rating']>, 'stars'> };

type HeatmapStep = Pick<ProcessStepRecordingDisplay, '_id'> & { surveys?: RatedSurvey[] };

type StepCapacityLookup = Record<string, Pick<CapacityBinding, 'hoursPerYear'>>;

type OverlayColor = NodeOverlay['color'];
type SignalColor = Exclude<OverlayColor, 'gray'>;

export function meanRating(surveys: RatedSurvey[] | undefined): number | null {
  return meanSurveyMetric(surveys, (survey) => survey.rating?.stars);
}

export function bucketRating(mean: number): SignalColor {
  if (mean >= RATING_GREEN_MIN) return 'green';
  if (mean >= RATING_YELLOW_MIN) return 'yellow';
  return 'red';
}

export function bucketCapacityShare(share: number): SignalColor {
  if (share < CAPACITY_GREEN_MAX_SHARE) return 'green';
  if (share < CAPACITY_YELLOW_MAX_SHARE) return 'yellow';
  return 'red';
}

export function bucketCombinedScore(score: number): SignalColor {
  if (score >= COMBINED_GREEN_MIN) return 'green';
  if (score >= COMBINED_YELLOW_MIN) return 'yellow';
  return 'red';
}

function capacityShare(
  stepId: string,
  stepCapacities: StepCapacityLookup | null | undefined,
  maxHoursPerYear: number
): number | null {
  const binding = stepCapacities?.[stepId];
  if (!binding || maxHoursPerYear === 0) return null;
  return binding.hoursPerYear / maxHoursPerYear;
}

// With a single signal, delegate to that signal's own buckets so a degraded
// combined view can never disagree with the dedicated rating/capacity views.
function combinedColor(mean: number | null, share: number | null): OverlayColor {
  if (mean !== null && share !== null) {
    return bucketCombinedScore(
      COMBINED_RATING_WEIGHT * ((mean - 1) / 4) + COMBINED_CAPACITY_WEIGHT * (1 - share)
    );
  }
  if (mean !== null) return bucketRating(mean);
  if (share !== null) return bucketCapacityShare(share);
  return 'gray';
}

export function maxStepHoursPerYear(
  steps: { _id: string }[],
  stepCapacities: StepCapacityLookup | null | undefined
): number {
  return steps.reduce(
    (max, step) => Math.max(max, stepCapacities?.[step._id]?.hoursPerYear ?? 0),
    0
  );
}

export function buildHeatmapOverlays(
  mode: Exclude<HeatmapViewMode, 'standard'>,
  steps: HeatmapStep[],
  stepCapacities: StepCapacityLookup | null | undefined
): NodeOverlay[] {
  const maxHoursPerYear = maxStepHoursPerYear(steps, stepCapacities);

  return steps.map((step) => {
    const mean = meanRating(step.surveys);
    const share = capacityShare(step._id, stepCapacities, maxHoursPerYear);

    let color: OverlayColor;
    if (mode === 'rating') {
      color = mean === null ? 'gray' : bucketRating(mean);
    } else if (mode === 'capacity') {
      color = share === null ? 'gray' : bucketCapacityShare(share);
    } else {
      color = combinedColor(mean, share);
    }
    return { nodeId: step._id, color };
  });
}
